import { roundPoints } from '@editorUtils'
import { number, object, mixed, string, array, boolean } from 'yup'
import { roofStoreyKey } from '@editorStores'

const pointSchema = object({
  x: number().required('X coordinate is required'),
  y: number().required('Y coordinate is required'),
  z: number().required('Z coordinate is required'),
})

// Define the schema for the points array
const pointsSchema = array()
  .of(pointSchema)
  .min(3, 'Each roof element must have 3 points')
  .required('Points array is required')

export const createLabelSchema = object({
  label: string().test(
    'create-label-whitespace',
    'Der Name darf keine Leerzeichen am Anfang oder Ende enthalten.',
    value => {
      if (value === undefined || value === '') return true
      return value === value.trim()
    },
  ),
})

export const applyCreateLabel = <T extends { label?: string }>(
  element: T,
  label: string | undefined,
): T => {
  if (label) return { ...element, label }
  if (element.label === undefined) return element
  const { label: _removed, ...rest } = element
  return rest as T
}

// Reusable base schema for common properties
const baseSlabSchema = object({
  guid: string(),
  shape: object({
    points: pointsSchema,
  }),
  orientation: mixed().oneOf(['horizontal', 'vertical']),
})

// Reusable storey validation for vertical slabs (not roof)
const verticalSlabStoreyValidation = string().test(
  'storey-check',
  'wrong storey selection',
  function (value, context) {
    const type = context.options.context?.element_type
    if (type === 'vertical_slabs' && value === roofStoreyKey) {
      return this.createError({
        message:
          'Aktuell unterstützt das Backend keine Deckenpositionen, denen das Dachgeschoss zugewiesen ist.',
      })
    }
    return true
  },
)

// Reusable storey validation for roof slabs
const roofSlabStoreyValidation = string().test(
  'storey-check',
  'wrong storey selection',
  function (value, context) {
    const type = context.options.context?.element_type
    if (type === 'vertical_roof_slabs' && value !== roofStoreyKey) {
      return this.createError({
        message: 'Dachpositionen muss das Dachgeschoss zugewiesen sein.',
      })
    }
    return true
  },
)

// Z-coordinate validation for vertical slabs (all points must have same z)
const verticalSlabZValidation = boolean().test({
  name: 'vertical-slab-z-coordinates',
  message: 'Alle Punkte einer Deckenposition müssen die gleiche Z-Koordinate haben.',
  test: function () {
    const { shape } = this.parent
    if (!shape?.points || shape.points.length !== 4) return false

    const points = shape.points
    const firstZ = points[0].z

    // Check that all points have the same z coordinate
    for (let i = 1; i < points.length; i++) {
      if (points[i].z !== firstZ) return false
    }
    return true
  },
})

// Z-coordinate validation for roof slabs (first 2 points must have same z)
const roofSlabZValidation = boolean().test({
  name: 'roof-slab-z-coordinates',
  message: 'Die ersten beiden Punkte einer Dachposition müssen die gleiche Z-Koordinate haben.',
  test: function () {
    const { shape } = this.parent
    if (!shape?.points || shape.points.length < 2) return false

    const points = shape.points
    const firstZ = points[0].z

    // Check that first 2 points have the same z coordinate
    return points[1].z === firstZ
  },
})

// Reusable rectangular validation
const rectangularValidation = boolean().test({
  name: 'is-rectangular',
  message:
    'Rechteckige Position: benachbarte Punkte müssen entweder gleiche x oder gleiche y Koordinaten haben.',
  test: function () {
    const { shape, xy_decimal_points_rounding } = this.parent
    if (!shape?.points || shape.points.length !== 4) return false

    const pointsRounded = roundPoints(shape.points, {
      roundX: xy_decimal_points_rounding ? 2 : undefined,
      roundY: xy_decimal_points_rounding ? 2 : undefined,
    })
    // Check that adjacent points have either same x or same y
    for (let i = 0; i < 4; i++) {
      const current = pointsRounded[i]
      const next = pointsRounded[(i + 1) % 4]
      const hasSameX = current.x === next.x
      const hasSameY = current.y === next.y
      if (!hasSameX && !hasSameY) return false
    }
    return true
  },
})

// Reusable trapezoidal validation
const trapezoidalValidation = boolean().test({
  name: 'is-trapezoidal',
  message:
    'Trapezförmige Position: Punkte 1-2 und 2-3 müssen gleiche x oder y haben, Punkt 3-4 muss diagonal sein, Punkt 4-1 muss gleiche x oder y haben.',
  test: function () {
    const { shape, xy_decimal_points_rounding } = this.parent
    if (!shape?.points || shape.points.length !== 4) return false

    const pointsRounded = roundPoints(shape.points, {
      roundX: xy_decimal_points_rounding ? 2 : undefined,
      roundY: xy_decimal_points_rounding ? 2 : undefined,
    })

    // Point 0 and point 1 have same x or same y
    const p0 = pointsRounded[0]
    const p1 = pointsRounded[1]
    const edge01SameX = p0.x === p1.x
    const edge01SameY = p0.y === p1.y
    if (!edge01SameX && !edge01SameY) return false

    // Point 1 and point 2 have same x or same y
    const p2 = pointsRounded[2]
    const edge12SameX = p1.x === p2.x
    const edge12SameY = p1.y === p2.y
    if (!edge12SameX && !edge12SameY) return false

    // Point 2 and point 3 have neither same x nor same y (diagonal)
    const p3 = pointsRounded[3]
    const edge23SameX = p2.x === p3.x
    const edge23SameY = p2.y === p3.y
    if (edge23SameX || edge23SameY) return false

    // Point 3 and point 0 have same x or same y
    const edge30SameX = p3.x === p0.x
    const edge30SameY = p3.y === p0.y
    if (!edge30SameX && !edge30SameY) return false

    return true
  },
})

// Schema for rectangular vertical slabs
export const rectangularVerticalSlabSchema = baseSlabSchema.concat(
  object({
    storey: verticalSlabStoreyValidation,
    isRectangular: rectangularValidation,
    zCoordinateCheck: verticalSlabZValidation,
  }),
)

// Schema for rectangular vertical roof slabs
export const rectangularVerticalRoofSlabSchema = baseSlabSchema.concat(
  object({
    storey: roofSlabStoreyValidation,
    isRectangular: rectangularValidation,
    zCoordinateCheck: roofSlabZValidation,
  }),
)

// Schema for trapezoidal vertical slabs
export const trapezoidalVerticalSlabSchema = baseSlabSchema.concat(
  object({
    storey: verticalSlabStoreyValidation,
    isTrapezoidal: trapezoidalValidation,
    zCoordinateCheck: verticalSlabZValidation,
  }),
)

// Schema for trapezoidal vertical roof slabs
export const trapezoidalVerticalRoofSlabSchema = baseSlabSchema.concat(
  object({
    storey: roofSlabStoreyValidation,
    isTrapezoidal: trapezoidalValidation,
    zCoordinateCheck: roofSlabZValidation,
  }),
)
